/**
 * SubmissionTable.tsx — sortable table of all (filtered) banks.
 *
 * Columns:
 *   org_id · org_name · last LOAN · last DEPOSIT · last KYC · latest report
 *   date · days since · overall status pill
 *
 * Each LOAN/DEPOSIT/KYC cell shows the date + a small pill (compact). The
 * overall status pill in the rightmost column reflects the worst flag.
 * Click a row → opens the drill-in drawer.
 */

import React from 'react';

import BankBadge from '../../components/BankBadge';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { useSortable } from '../../hooks/useSortable';
import type { BankSubmissionRow, DumpStatus } from '../../type/api';

interface Props {
	rows: BankSubmissionRow[];
	onRowClick: (row: BankSubmissionRow) => void;
}

/** Pull the freshest report-date across loan/deposit/kyc for sort + display. */
function latestReportDate(r: BankSubmissionRow): string | null {
	const dates = [r.loan.lastReportDate, r.deposit.lastReportDate, r.kyc.lastReportDate].filter(
		(d): d is string => Boolean(d),
	);
	if (!dates.length) return null;
	return dates.reduce((a, b) => (a > b ? a : b));
}

/** Smallest non-null daysSince across the three dump-types. */
function minDaysSince(r: BankSubmissionRow): number | null {
	const ds = [r.loan.daysSince, r.deposit.daysSince, r.kyc.daysSince].filter(
		(d): d is number => d !== null && d !== undefined,
	);
	if (!ds.length) return null;
	return Math.min(...ds);
}

function SubmissionCell({ status }: { status: DumpStatus }) {
	if (!status.lastReportDate) {
		return (
			<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<span style={{ color: 'var(--text-faint)', fontSize: 12 }}>—</span>
				<StatusPill flag="GREY" compact />
			</div>
		);
	}
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			<span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
				{formatDate(status.lastReportDate)}
			</span>
			<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
				<StatusPill flag={status.flag} compact />
				{typeof status.daysSince === 'number' && (
					<span style={{ fontSize: 11, color: 'var(--text-faint)' }}>
						{status.daysSince === 0
							? 'today'
							: status.daysSince === 1
								? '1d'
								: `${status.daysSince}d`}
					</span>
				)}
			</div>
		</div>
	);
}

export default function SubmissionTable({ rows, onRowClick }: Props) {
	const sortable = useSortable<BankSubmissionRow>(
		rows,
		{ field: 'orgName', dir: 'asc' },
		{
			orgId: (r) => r.orgId,
			orgName: (r) => r.orgName,
			loan: (r) => r.loan.lastReportDate,
			deposit: (r) => r.deposit.lastReportDate,
			kyc: (r) => r.kyc.lastReportDate,
			latest: (r) => latestReportDate(r),
			daysSince: (r) => minDaysSince(r),
			overallFlag: (r) => r.overallFlag,
		},
	);

	return (
		<div className="card" style={{ padding: 0, overflow: 'hidden' }}>
			<div style={{ overflowX: 'auto' }}>
				<table className="tbl" style={{ width: '100%' }}>
					<thead>
						<tr>
							<SortableTh field="orgId" sort={sortable} numeric>ID</SortableTh>
							<SortableTh field="orgName" sort={sortable}>Bank</SortableTh>
							<SortableTh field="loan" sort={sortable}>Last LOAN</SortableTh>
							<SortableTh field="deposit" sort={sortable}>Last DEPOSIT</SortableTh>
							<SortableTh field="kyc" sort={sortable}>Last KYC</SortableTh>
							<SortableTh field="latest" sort={sortable}>Latest report</SortableTh>
							<SortableTh field="daysSince" sort={sortable} numeric>Days since</SortableTh>
							<SortableTh field="overallFlag" sort={sortable}>Status</SortableTh>
						</tr>
					</thead>
					<tbody>
						{sortable.rows.map((r) => {
							const latest = latestReportDate(r);
							const days = minDaysSince(r);
							return (
								<tr
									key={r.orgId}
									className="clickable"
									onClick={() => onRowClick(r)}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onRowClick(r);
										}
									}}
									tabIndex={0}
									aria-label={`Open load history for ${r.orgName}`}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{r.orgId}
									</td>
									<td>
										<BankBadge orgId={r.orgId} orgName={r.orgName} />
									</td>
									<td>
										<SubmissionCell status={r.loan} />
									</td>
									<td>
										<SubmissionCell status={r.deposit} />
									</td>
									<td>
										<SubmissionCell status={r.kyc} />
									</td>
									<td style={{ fontVariantNumeric: 'tabular-nums' }}>
										{latest ? formatDate(latest) : '—'}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{days === null ? '—' : days}
									</td>
									<td>
										<StatusPill flag={r.overallFlag} />
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
}
